import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3052/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  profile: () => api.get('/auth/profile'),
  switchCompany: (id) => api.post(`/auth/switch-company/${id}`),
  setDefaultCompany: (id) => api.post(`/auth/set-default-company/${id}`),
};

export const companyAPI = {
  getAll: () => api.get('/companies'),
  getById: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
  getAuthorities: (id) => api.get(`/companies/${id}/authorities`),
  addAuthority: (id, data) => api.post(`/companies/${id}/authorities`, data),
  updateAuthority: (id, data) => api.put(`/companies/authorities/${id}`, data),
  deleteAuthority: (id) => api.delete(`/companies/authorities/${id}`),
  // Signatures
  getSignatures: (companyId) => api.get(`/companies/${companyId}/signatures`),
  uploadSignature: (companyId, formData) => api.post(`/companies/${companyId}/signatures`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteSignature: (id) => api.delete(`/companies/signatures/${id}`),
  downloadSignature: (id) => api.get(`/companies/signatures/${id}/download`, { responseType: 'blob' }),
};

export const documentAPI = {
  getAll: (params) => api.get('/documents', { params }),
  getReport: (params) => api.get('/documents/report', { params }),
  getById: (id) => api.get(`/documents/${id}`),
  getNextNumber: () => api.get('/documents/next-number'),
  create: (data) => api.post('/documents', data),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
  getTruckReceipt: (id) => api.get(`/documents/${id}/truck-receipt`),
  saveTruckReceipt: (id, data) => api.put(`/documents/${id}/truck-receipt`, data),
  getPdfData: (id, docType) => api.get(`/documents/${id}/pdf-data/${docType}`),
  savePdfData: (id, docType, data) => api.put(`/documents/${id}/pdf-data/${docType}`, data),
  deletePdfData: (id, docType) => api.delete(`/documents/${id}/pdf-data/${docType}`),
};

export const contactAPI = {
  getAll: (params) => api.get('/contacts', { params }),
  getById: (id) => api.get(`/contacts/${id}`),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
  search: (q) => api.get('/contacts/search', { params: { q } }),
  saveBuyer: (data) => api.post('/contacts/save-buyer', data),
  importCSV: (formData, onUploadProgress) => api.post('/contacts/import-csv', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
  }),
};

export const attachmentAPI = {
  upload: (documentId, files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return api.post(`/documents/${documentId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  list: (documentId) => api.get(`/documents/${documentId}/attachments`),
  download: (id) =>
    api.get(`/attachments/${id}/download`, { responseType: 'blob' }),
  remove: (id) => api.delete(`/attachments/${id}`),
};

export const buyerDetailAPI = {
  getAll: () => api.get('/buyer-details'),
  search: (q) => api.get('/buyer-details/search', { params: { q } }),
  create: (data) => api.post('/buyer-details', data),
  update: (id, data) => api.put(`/buyer-details/${id}`, data),
  delete: (id) => api.delete(`/buyer-details/${id}`),
};

export const proformaAPI = {
  getAll: (params) => api.get('/proforma-invoices', { params }),
  getById: (id) => api.get(`/proforma-invoices/${id}`),
  getNextNumber: () => api.get('/proforma-invoices/next-number'),
  create: (data) => api.post('/proforma-invoices', data),
  update: (id, data) => api.put(`/proforma-invoices/${id}`, data),
  delete: (id) => api.delete(`/proforma-invoices/${id}`),
};

export const emailAPI = {
  sendDocument: (formData) => api.post('/email/send', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export const settingsAPI = {
  getFinancialYears: () => api.get('/settings/financial-years'),
  createFinancialYear: (data) => api.post('/settings/financial-years', data),
  setActiveFinancialYear: (id) => api.put(`/settings/financial-years/${id}/activate`),
  deleteFinancialYear: (id) => api.delete(`/settings/financial-years/${id}`),
  getPackageTypes: () => api.get('/settings/package-types'),
  createPackageType: (data) => api.post('/settings/package-types', data),
  deletePackageType: (id) => api.delete(`/settings/package-types/${id}`),
  getNumbering: () => api.get('/settings/numbering'),
  updateNumbering: (data) => api.put('/settings/numbering', data),
};

export const pdfLayoutAPI = {
  getAll: (docType) => api.get('/pdf-layouts', { params: docType ? { doc_type: docType } : {} }),
  create: (data) => api.post('/pdf-layouts', data),
  update: (id, data) => api.put(`/pdf-layouts/${id}`, data),
  activate: (id) => api.put(`/pdf-layouts/${id}/activate`),
  delete: (id) => api.delete(`/pdf-layouts/${id}`),
};

export const userAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getRoles: () => api.get('/users/roles'),
  getPermissions: () => api.get('/users/permissions'),
  createRole: (data) => api.post('/users/roles', data),
  updateRole: (id, data) => api.put(`/users/roles/${id}`, data),
  deleteRole: (id) => api.delete(`/users/roles/${id}`),
  createPermission: (data) => api.post('/users/permissions', data),
  updatePermission: (id, data) => api.put(`/users/permissions/${id}`, data),
  deletePermission: (id) => api.delete(`/users/permissions/${id}`),
};

export default api;
